import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Alert, 
  InputGroup,
  Spinner,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faMapMarkerAlt,
  faCheckCircle, 
  faExclamationTriangle,
  faSearch,
  faUser,
  faFileAlt,
  faShieldAlt,
  faSync,
  faTimes,
  faPlus,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useWorkflow, PropertyInput, MLSData } from '../WorkflowProvider';
import axiosInstance from 'helpers/api';

// Debug utilities
const DEBUG_PREFIX = '🔍 [STEP1-STREAMLINED]';
const debugLog = (stage: string, message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  console.log(`${DEBUG_PREFIX} [${timestamp}] [${stage}] ${message}`);
  if (data) console.log(`${DEBUG_PREFIX} [${timestamp}] [${stage}] Data:`, data);
};

// Interfaces
interface AddressSuggestion {
  address: string;
  coordinates: [number, number] | null;
  confidence: number;
  source: string;
}

interface CustomerOption {
  id: string;
  label: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  lead_type?: string;
}

interface Step1StreamlinedProps {
  onComplete: (data: PropertyInput) => void;
}

const Step1PropertyInputStreamlined: React.FC<Step1StreamlinedProps> = ({ onComplete }) => {
  const { state, setPropertyInput, setMLSData: setWorkflowMLSData } = useWorkflow();
  const { register, handleSubmit, formState: { errors }, setValue, getValues, watch } = useForm<PropertyInput>();

  // Core state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupStatus, setLookupStatus] = useState<'none' | 'loading' | 'success' | 'error'>('none');
  
  // Address handling
  const [addressValue, setAddressValue] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [addressLocked, setAddressLocked] = useState(false);
  
  // Property confirmation
  const [mlsData, setLocalMLSData] = useState<MLSData | null>(null);
  const [propertyConfirmed, setPropertyConfirmed] = useState(false);
  
  // Report details
  const [reportName, setReportName] = useState('');
  
  // Customer selection
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Watchers
  const watchedAddress = watch("property_address", "");

  // Initialize form with existing data
  useEffect(() => {
    if (state.propertyInput) {
      const { property_address, customerName } = state.propertyInput;
      if (property_address) {
        setAddressValue(property_address);
        setValue("property_address", property_address);
      }
      if (customerName) setValue("customerName", customerName);
    }
    if (state.mlsData) {
      setLocalMLSData(state.mlsData);
      setPropertyConfirmed(true);
      setAddressLocked(true);
    }
  }, [state, setValue]);

  // Address suggestions
  const fetchAddressSuggestions = async (query: string): Promise<AddressSuggestion[]> => {
    if (query.length < 3) return [];
    
    try {
      setIsLoadingSuggestions(true);
      const response = await axiosInstance.get(`/properties/address-suggestions-public?query=${encodeURIComponent(query)}`);
      
      if (response.data.success && response.data.suggestions) {
        return response.data.suggestions;
      }
      return [];
    } catch (error) {
      console.error('Address suggestions failed:', error);
      return [];
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Handle address input changes
  const handleAddressChange = async (value: string) => {
    setAddressValue(value);
    setValue("property_address", value);
    
    if (value.length >= 3 && !addressLocked) {
      const suggestions = await fetchAddressSuggestions(value);
      setAddressSuggestions(suggestions);
    } else {
      setAddressSuggestions([]);
    }
  };

  // Handle address selection
  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    setAddressValue(suggestion.address);
    setValue("property_address", suggestion.address);
    setAddressSuggestions([]);
    debugLog('ADDRESS_SELECT', 'Address selected from suggestions', suggestion);
  };

  // Property lookup
  const handlePropertyLookup = async () => {
    if (!addressValue || addressValue.length < 10) {
      setError('Please enter a complete address');
      return;
    }

    setLoading(true);
    setLookupStatus('loading');
    setError(null);
    
    try {
      debugLog('PROPERTY_LOOKUP', 'Starting enhanced property lookup', { address: addressValue });
      
      const response = await axiosInstance.post('/properties/enhanced-single-endpoint-cma', {
        address: addressValue,
        customer_name: selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : '',
        propertyInput: {
          address: addressValue,
          propertyType: 'Single Family Residence'
        }
      });

      if (response.data.success) {
        // Handle the direct response structure from Python script
        const propertyData = response.data.subject_property;
        
        if (propertyData) {
          setLocalMLSData(propertyData);
          setPropertyConfirmed(true);
          setAddressLocked(true);
          setLookupStatus('success');
          
          // Store the full CMA response for later use in Step 2
          sessionStorage.setItem('fullCMAResponse', JSON.stringify(response.data));
          // ALSO store with the key Step 2 expects
          sessionStorage.setItem('enhancedCmaData', JSON.stringify(response.data));
          
          // Update workflow state
          setWorkflowMLSData(propertyData);
          setPropertyInput({
            ...getValues(),
            property_address: addressValue
          });
          
          debugLog('PROPERTY_LOOKUP', 'Property found successfully', propertyData);
          console.log('🔍 [PROPERTY DATA] Available fields:', Object.keys(propertyData));
          console.log('🔍 [PROPERTY DATA] MLS Status:', propertyData.mls_status);
          console.log('🔍 [PROPERTY DATA] Listing ID:', propertyData.listing_id);
          console.log('🔍 [PROPERTY DATA] Full property data:', propertyData);
        } else {
          setLookupStatus('error');
          setError('Property data not found in response.');
        }
      } else {
        setLookupStatus('error');
        setError('Property not found. Please verify the address and try again.');
        debugLog('PROPERTY_LOOKUP', 'Property not found');
      }
    } catch (error) {
      console.error('Property lookup error:', error);
      setLookupStatus('error');
      setError('Unable to lookup property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Customer search
  const fetchCustomers = async (searchTerm: string = '') => {
    setIsLoadingCustomers(true);
    try {
      const response = await axiosInstance.get('/leads', {
        params: searchTerm ? { q: searchTerm, limit: 20 } : { limit: 20 }
      });

      if (response.data.success && response.data.data) {
        const customers = response.data.data.map((lead: any) => ({
          id: lead.id,
          label: `${lead.first_name} ${lead.last_name}${lead.email ? ` - ${lead.email}` : ''}`,
          first_name: lead.first_name,
          last_name: lead.last_name,
          email: lead.email,
          phone: lead.phone,
          lead_type: lead.lead_type
        }));
        setCustomerOptions(customers);
      }
    } catch (error) {
      console.error('Customer search failed:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Handle customer search
  const handleCustomerSearch = (searchTerm: string) => {
    setCustomerSearchTerm(searchTerm);
    if (searchTerm.length >= 2) {
      fetchCustomers(searchTerm);
      setShowCustomerDropdown(true);
    } else {
      setShowCustomerDropdown(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: CustomerOption) => {
    setSelectedCustomer(customer);
    setCustomerSearchTerm(customer.label);
    setValue("customerName", `${customer.first_name} ${customer.last_name}`);
    setShowCustomerDropdown(false);
  };

  // Form submission
  const onSubmit = (data: PropertyInput) => {
    // TEMPORARY: Allow proceeding without property confirmation for testing
    if (!propertyConfirmed && addressValue.length < 10) {
      setError('Please enter a complete address');
      return;
    }

    // Get the full CMA data from sessionStorage if available (from the backend response)
    const fullCMAData = JSON.parse(sessionStorage.getItem('fullCMAResponse') || '{}');

    // If no property confirmed, create mock data for testing
    const mockPropertyData = !propertyConfirmed ? {
      address: addressValue,
      list_price: 450000,
      bedrooms: 4,
      bathrooms: 2,
      living_area: 1850,
      year_built: 2015,
      mls_status: 'Testing',
      listing_id: 'TEST-' + Date.now()
    } : mlsData;

    // Prepare workflow data with standardized structure
    const workflowData = {
      reportId: `cma_${Date.now()}`, // Generate unique report ID
      reportName: reportName || `CMA Report - ${addressValue}`,
      propertyData: mockPropertyData,
      customerData: selectedCustomer,
      propertyInput: {
        ...data,
        property_address: addressValue,
        report_name: reportName
      },
      // Include comparable properties if available from the enhanced lookup
      comparableProperties: fullCMAData.comparable_properties?.comparables || [],
      pricePrediction: fullCMAData.price_prediction || null,
      searchMetadata: {
        confidence: propertyConfirmed ? 100 : 50,
        searchMethods: propertyConfirmed ? ['enhanced_single_endpoint_cma'] : ['testing_mode'],
        timestamp: new Date().toISOString(),
        processingTime: fullCMAData.processing_time_ms || null,
        testingMode: !propertyConfirmed
      }
    };

    // Store in sessionStorage with standardized key
    sessionStorage.setItem('smartCMAWorkflowData', JSON.stringify(workflowData));
    // ALSO store with the key Step 2 expects for workflow data
    sessionStorage.setItem('smartCMAData', JSON.stringify(workflowData));
    
    debugLog('FORM_SUBMIT', 'Workflow data stored, proceeding to Step 2', workflowData);
    
    // Update workflow state and proceed
    setPropertyInput(workflowData.propertyInput);
    if (mlsData) {
      setWorkflowMLSData(mlsData);
    }
    
    onComplete(workflowData.propertyInput);
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes aiGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.3); }
          50% { box-shadow: 0 0 30px rgba(0, 212, 255, 0.6); }
        }
      `}</style>
      
      <Card 
        className="h-100 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important',
          borderRadius: '16px'
        }}
      >
        {/* AI-Forward Header */}
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{ 
                  width: "48px", 
                  height: "48px",
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                  animation: 'aiGlow 3s infinite'
                }}
              >
                <FontAwesomeIcon 
                  icon={faHome} 
                  className="text-white" 
                  size="lg"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div>
                <h4 style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '4px'
                }}>
                  Smart CMA Generator
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  AI-powered property analysis & market intelligence
                </p>
              </div>
            </div>
            
            {/* AI Status Indicator */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div className="rounded-circle me-2" style={{
                width: '8px', height: '8px', background: '#10b981',
                animation: 'pulse 2s infinite', display: 'inline-block'
              }} />
              <span className="fw-semibold text-success" style={{ 
                fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'
              }}>
                AI READY
              </span>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Error Alert */}
            {error && (
              <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {error}
              </Alert>
            )}

            {/* Report Name Section */}
            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2 d-flex align-items-center fw-semibold" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                    Report Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter report name (e.g., 'CMA for 123 Main St')"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    className="border-0"
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(0, 212, 255, 0.2) !important',
                      borderRadius: '8px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Address Input Section */}
            <Row className="mb-4">
              <Col>
                <div 
                  className="p-4 rounded-4 border-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)',
                    border: '1px solid rgba(0, 212, 255, 0.2) !important'
                  }}
                >
                  <Form.Group className="position-relative">
                    <Form.Label className="form-label-header mb-3 d-flex align-items-center fw-bold" style={{ color: '#0ea5e9' }}>
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ 
                          width: "32px", 
                          height: "32px",
                          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                          boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white" size="sm" />
                      </div>
                      Property Address
                    </Form.Label>
                    
                    <InputGroup className="shadow-sm">
                      <Form.Control
                        type="text"
                        placeholder="🏠 Enter complete property address (e.g., 123 Main St, Phoenix, AZ 85001)"
                        value={addressValue}
                        {...register("property_address", {
                          required: "Property address is required",
                          minLength: { value: 10, message: "Please enter a complete address" }
                        })}
                        onChange={(e) => handleAddressChange(e.target.value)}
                        isInvalid={!!errors.property_address}
                        className="form-control-lg border-0 rounded-start-3"
                        disabled={addressLocked}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(0, 212, 255, 0.3) !important'
                        }}
                      />
                      <InputGroup.Text 
                        className="border-0 rounded-end-3"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 212, 255, 0.3) !important',
                          borderLeft: 'none !important'
                        }}
                      >
                        {isLoadingSuggestions ? (
                          <Spinner size="sm" style={{ color: '#00d4ff' }} />
                        ) : addressLocked ? (
                          <FontAwesomeIcon icon={faShieldAlt} className="text-success" />
                        ) : (
                          <FontAwesomeIcon icon={faSearch} className="text-muted" />
                        )}
                      </InputGroup.Text>
                    </InputGroup>
                    
                    {errors.property_address && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {errors.property_address.message}
                      </Form.Control.Feedback>
                    )}

                    {/* Address Suggestions */}
                    {addressSuggestions.length > 0 && !addressLocked && (
                      <div 
                        className="position-absolute w-100 mt-1"
                        style={{ zIndex: 1000 }}
                      >
                        <Card className="border-0 shadow-lg">
                          <Card.Body className="p-0">
                            {addressSuggestions.map((suggestion, index) => (
                              <div
                                key={index}
                                className="p-3 border-bottom cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  background: 'rgba(255, 255, 255, 0.95)',
                                  backdropFilter: 'blur(10px)'
                                }}
                                onClick={() => handleAddressSelect(suggestion)}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'}
                              >
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary me-2" />
                                  <div>
                                    <div className="fw-semibold">{suggestion.address}</div>
                                    <small className="text-muted">Confidence: {suggestion.confidence}%</small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </Card.Body>
                        </Card>
                      </div>
                    )}

                    {/* Search Button */}
                    {!propertyConfirmed && addressValue.length >= 10 && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="primary" 
                          onClick={handlePropertyLookup}
                          disabled={loading}
                          size="lg"
                          style={{
                            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px 24px'
                          }}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Searching Property...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} className="me-2" />
                              Find Property
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Change Address Button */}
                    {addressLocked && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => {
                            setAddressLocked(false);
                            setPropertyConfirmed(false);
                            setLocalMLSData(null);
                            setLookupStatus('none');
                          }}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faSync} className="me-2" />
                          Search Different Property
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                </div>
              </Col>
            </Row>

            {/* Property Confirmation Card */}
            {propertyConfirmed && mlsData && (
              <Row className="mb-4">
                <Col>
                  <Card 
                    className="border-0 shadow-sm"
                    style={{
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(21, 128, 61, 0.1) 100%)',
                      border: '1px solid rgba(34, 197, 94, 0.3) !important'
                    }}
                  >
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ 
                              width: "40px", 
                              height: "40px",
                              background: '#22c55e',
                              boxShadow: '0 0 15px rgba(34, 197, 94, 0.4)'
                            }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
                          </div>
                          <div>
                            <h6 className="mb-1 fw-semibold text-success">Property Confirmed</h6>
                            <p className="mb-0 text-muted small">{addressValue}</p>
                            {mlsData.bedrooms && mlsData.bathrooms && mlsData.living_area && (
                              <p className="mb-0 text-muted small">
                                {mlsData.bedrooms} bed • {mlsData.bathrooms} bath • {mlsData.living_area.toLocaleString()} sqft
                              </p>
                            )}
                            {/* MLS Legal Attribution - Required */}
                            <p className="mb-0 text-muted small mt-1" style={{ fontStyle: 'italic' }}>
                              Courtesy of {
                                (() => {
                                  // Check for brokerage data in order of preference
                                  const brokerage = mlsData.listing_office_name || 
                                                  mlsData.brokerage || 
                                                  (mlsData as any).ListingOfficeName;
                                  
                                  if (brokerage && brokerage.trim()) {
                                    return brokerage;
                                  }
                                  
                                  // Fallback based on MLS status and age
                                  const mlsNumber = mlsData.mls_number || mlsData.listing_id;
                                  if (mlsNumber) {
                                    return `ARMLS (MLS# ${mlsNumber})`;
                                  }
                                  
                                  return 'ARMLS';
                                })()
                              }
                              {(() => {
                                const agent = mlsData.listing_agent_full_name || 
                                            mlsData.agent_name || 
                                            (mlsData as any).ListingAgentFullName;
                                
                                if (agent && agent.trim()) {
                                  return ` - ${agent}`;
                                }
                                
                                return '';
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="text-end">
                          {mlsData.list_price && (
                            <div className="fw-bold text-success h5 mb-0">
                              ${mlsData.list_price.toLocaleString()}
                            </div>
                          )}
                          {(mlsData.mls_status || mlsData.status) && (
                            <small className="text-muted">{mlsData.mls_status || mlsData.status}</small>
                          )}
                          {/* MLS Number for legal compliance */}
                          {(mlsData.mls_number || mlsData.listing_id) && (
                            <div className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                              MLS# {mlsData.mls_number || mlsData.listing_id}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Customer Selection */}
            <Row className="mb-4">
              <Col>
                <Form.Group className="position-relative">
                  <Form.Label className="form-label-header mb-2 d-flex align-items-center fw-semibold" style={{ color: '#374151' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                    Customer (Optional)
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search existing customers or leave blank for new customer"
                      value={customerSearchTerm}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      className="border-0"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 212, 255, 0.2) !important',
                        borderRadius: '8px 0 0 8px'
                      }}
                    />
                    <InputGroup.Text 
                      className="border-0"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 212, 255, 0.2) !important',
                        borderLeft: 'none !important',
                        borderRadius: '0 8px 8px 0'
                      }}
                    >
                      {isLoadingCustomers ? (
                        <Spinner size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faUser} className="text-muted" />
                      )}
                    </InputGroup.Text>
                  </InputGroup>

                  {/* Customer Dropdown */}
                  {showCustomerDropdown && customerOptions.length > 0 && (
                    <div 
                      className="position-absolute w-100 mt-1"
                      style={{ zIndex: 999 }}
                    >
                      <Card className="border-0 shadow-lg">
                        <Card.Body className="p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {customerOptions.map((customer) => (
                            <div
                              key={customer.id}
                              className="p-3 border-bottom cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleCustomerSelect(customer)}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <div className="fw-semibold">{customer.first_name} {customer.last_name}</div>
                              {customer.email && <small className="text-muted">{customer.email}</small>}
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </div>
                  )}

                  {selectedCustomer && (
                    <div className="mt-2">
                      <div 
                        className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
                        style={{
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="text-success me-2" />
                        <span className="me-2">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-danger"
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerSearchTerm('');
                            setValue("customerName", '');
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Continue Button */}
            <Row>
              <Col className="text-center">
                <Button 
                  type="submit"
                  size="lg"
                  disabled={loading || addressValue.length < 10}
                  style={{
                    background: (propertyConfirmed || addressValue.length >= 10)
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' 
                      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '15px 40px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: (propertyConfirmed || addressValue.length >= 10) ? '0 8px 25px rgba(34, 197, 94, 0.3)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {propertyConfirmed ? (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Continue to Comparable Analysis
                    </>
                  ) : addressValue.length >= 10 ? (
                    <>
                      <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                      🚀 Skip to Step 2 (Testing)
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      Enter Address First
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default Step1PropertyInputStreamlined;